import configs from '@/config'
import Vue from 'vue'
import { SimpleCache } from '@/utils/cache.utils'
import { isSharedFrameCacheEnabled, sharedFramePersistence } from '@/utils/indexedDB/shared-frame-cache'

const actions = {
  getFeatureSettings: {
    method: 'GET',
    url: `${configs.baseUrl}/api/feature-settings`,
  },
}

let resource

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

// AHA-48350: every PowerPoint add-in slide is its own page, and App.vue holds each one on its
// loading screen until feature settings arrive. The settings another slide of the deck fetched for
// the same user in the last few minutes are served from IndexedDB while this page's own fetch refreshes them.
const CACHE_KEY = 'feature-settings'
const SHARED_COPY_MAX_AGE_SECONDS = 30 * 60

const cache = new SimpleCache({ persistence: sharedFramePersistence })

const fetchFeatureSettings = async () => {
  if (!resource) initResource()
  const res = await resource.getFeatureSettings()
  return res.body
}

/**
 * @param {object} [options] only used by the add-in build; elsewhere every call goes to the network
 * @param {number} [options.userId] whose settings these are; without it nothing is shared
 * @param {(servedValue: object, refresh: Promise<object>) => void} [options.onSharedCopyServed] called
 *   when this page's first call resolves another frame's copy, with the fetch that refreshes it
 */
const getFeatureSettings = async ({ userId, onSharedCopyServed } = {}) => {
  if (!isSharedFrameCacheEnabled() || !userId) return fetchFeatureSettings()
  return cache.invoke(fetchFeatureSettings, {
    key: CACHE_KEY,
    maxAgeInSeconds: 0,
    persistent: {
      scope: `user:${userId}`,
      maxAgeInSeconds: SHARED_COPY_MAX_AGE_SECONDS,
      onPersistedCopyServed: onSharedCopyServed,
    },
  })
}

export default {
  initResource,
  getFeatureSettings,
}
